import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 20px;
`;

const Form = styled.form`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
  padding: 40px;
`;

const Title = styled.h2`
  margin-bottom: 10px;
  color: #333;
  text-align: center;
  font-size: 28px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  margin-bottom: 25px;
  text-align: center;
  color: #666;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const MessageText = styled.p`
  color: green;
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
`;

const ErrorText = styled.p`
  color: red;
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
`;

const BackLink = styled.a`
  color: #007bff;
  text-decoration: none;
  text-align: center;
  display: block;
  margin-top: 20px;
  font-size: 14px;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const ForgotPasswordScreenDesktop = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { sendPasswordResetEmail } = useAuth();

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await sendPasswordResetEmail(email);
      setMessage("Password reset email sent successfully. Please check your inbox.");
    } catch (e) {
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Form onSubmit={handleResetPassword}>
        <Title>Forgot Password</Title>
        <Subtitle>Enter your email address to reset your password.</Subtitle>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Button type="submit" disabled={loading}>
          {loading ? 'Sending...' : 'Reset Password'}
        </Button>
        {message && <MessageText>{message}</MessageText>}
        {error && <ErrorText>{error}</ErrorText>}
        <BackLink href="/login">Back to Login</BackLink>
      </Form>
    </Container>
  );
};
